<template>
	<div v-if="pdfContent && !showLoadingSpinner" class="relative rounded p-1 w-64 shadow border">
		<div
			:class="{
				'cursor-pointer': !disableClick && !scrollable,
			}"
			class="relative"
			@click="openPDF()"
		>
			<img v-if="isAppContainer" :src="pdfContent" alt="PDF Thumbnail" />

			<iframe
				v-else
				:src="pdfContent"
				:class="{
					'pointer-events-none cursor-pointer': !scrollable,
					'pointer-events-auto': scrollable,
				}"
				class="justify-center"
				width="100%"
				:height="iframeHeight"
			/>
		</div>

		<div v-if="!pdfURL && showLoadingSpinner" class="absolute top-0 left-0 right-0 bottom-0 flex flex-col items-center justify-center">
			<font-awesome-icon :icon="['fas', 'sync-alt']" class="w-32 h-32 text-5xl text-black opacity-25" size="lg" :spin="true" />
		</div>
	</div>
</template>

<script>
import AppContainerMixin from '@/mixins/AppContainerMixin';
import apiDefaults from '@/api/defaultConfiguration';
import { PdfApiFactory } from '@/api/generated';

export default {
	name: 'PDFThumbnailPreview',
	mixins: [AppContainerMixin],

	props: {
		type: {
			required: true,
		},
		pdfURL: {
			required: true,
			default: '',
		},
		uuid: {
			required: true,
			default: '',
		},
		showRouteParams: {
			required: false,
			default: false,
		},
		showLoadingSpinner: {
			default: false,
		},
		reloadAndOpen: {
			// Öffnet PDF wenn auf Reload gedrückt wird
			type: Boolean,
			default: true,
		},
		disableClick: {
			// Deaktiviert das Öffnen in neuem Tab
			type: Boolean,
			default: false,
		},
		scrollable: {
			// Aktiviert Scrollen innerhalb der PDF-Preview
			type: Boolean,
			default: false,
		},
		isLoading: {
			default: false,
		},
		iframeHeight: {
			default: '350px',
			required: false,
		},

	},

	data() {
		return {
			pdfContent: null,
			pdfBlob: null,
			pdfName: '',
		};
	},

	created() {
		this.getPDFContent();

		if (!this.isAppContainer && !this.scrollable) {
			window.focus();

			const listener = () => {
				if (document.activeElement === document.querySelector('iframe') && !this.disableClick) {
					this.openPDF();
				}

				window.removeEventListener('blur', listener);
			};

			window.addEventListener('blur', listener);
		}
	},

	methods: {
		openPDF() {
			if (this.disableClick) {
				return;
			}

			if (this.isAppContainer) {
				const reader = new FileReader();
				const name = this.pdfName;

				reader.onload = function() {
					const arrayBuffer = this.result;

					const dataString = new Uint8Array(arrayBuffer).join(',');

					const messageData = {
						fileName: name,
						data: dataString,
					};

					window.webkit.messageHandlers.pdfHandler.postMessage(JSON.stringify(messageData));
				};

				reader.readAsArrayBuffer(this.pdfBlob);
			} else {
				const viewerUrl = `/blobviewer?blobUrl=${encodeURIComponent(this.pdfContent)}` + `&title=${encodeURIComponent(this.pdfName)}`;

				setTimeout(() => {
					window.open(viewerUrl, '_blank');
				}, 200);
			}

			this.$emit('click');
		},

		async getPDFContent() {
			/*
			 * Neuer Flow:
			 * explizite URL hat Vorrang.
			 */
			if (this.pdfURL) {
				return this.getPDFContentFromURL();
			}

			/*
			 * Bestehender Legacy-Flow bleibt unverändert.
			 */
			if (this.uuid) {
				return this.getPDFContentFromLegacyService();
			}
		},

		async getPDFContentFromURL() {
			try {
				const response = await apiDefaults.axios.get(this.pdfURL, {
					responseType: 'arraybuffer',
				});

				this.setPDFContent(response);
			} catch (error) {
				console.error('Could not load PDF from URL.', error);

				this.$emit('error', error);
			}
		},

		async getPDFContentFromLegacyService() {
			try {
				const response = await PdfApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios).pdfDamagecasesUuidTempFileGet(this.uuid);

				this.setPDFContent(response);
			} catch (error) {
				console.error('Could not load PDF from legacy service.', error);

				this.$emit('error', error);
			}
		},

		setPDFContent(response) {
			const blob = new Blob([response.data], {
				type: 'application/pdf',
			});

			if (this.pdfContent) {
				URL.revokeObjectURL(this.pdfContent.split('#')[0]);
			}

			if (this.isAppContainer) {
				this.pdfBlob = blob;
			}

			this.pdfName = this.getPDFName(response);

			const scrollbar = this.scrollable ? 1 : 0;

			this.pdfContent = URL.createObjectURL(blob) + `#toolbar=0&navpanes=0&scrollbar=${scrollbar}`;
		},

		getPDFName(response) {
			const contentDisposition = response.headers?.['content-disposition'];

			if (contentDisposition) {
				const match = contentDisposition.match(/filename="?([^"]+)"?/);

				if (match && match[1]) {
					return match[1];
				}
			}

			return 'document.pdf';
		},
	},

	watch: {
		uuid() {
			if (!this.pdfURL) {
				this.getPDFContent();
			}
		},

		pdfURL() {
			if (this.pdfURL) {
				this.getPDFContent();
			}
		},
	},
};
</script>
